import React from "react";
import styles from "./styles.module.scss";
import theme from "../../Styles/theme.scss";
import LilliSelect from "../LilliDesignSystem/LilliSelect";
import type { LilliSelectOption } from "../LilliDesignSystem/LilliSelect";
import PrimaryButton from "../PrimaryButton";
import { useAmplitudeTracking } from "Services/Hooks/useAmplitudeTracking";
import { PRESETS, PRESET_OPTIONS } from "../../Pages/InstallPerformance/utils";
import type { PresetKey } from "../../Pages/InstallPerformance/utils";
import type { Granularity } from "@intelligentlilli/api-layer";
import { useViewport } from "../ViewportProvider";
import { mobileBreakpoint } from "../../Services/config";

interface DatePresetSelectorProps {
  preset: PresetKey;
  setPreset: (key: PresetKey) => void;
  granularity: Granularity;
  setGranularity: (g: Granularity) => void;
}

const GRANULARITY_OPTIONS: { value: Granularity; label: string; short: string }[] = [
  { value: "D", label: "Days", short: "D" },
  { value: "W", label: "Weeks", short: "W" },
  { value: "M", label: "Months", short: "M" },
];

const DatePresetSelector = ({
  preset,
  setPreset,
  granularity,
  setGranularity,
}: DatePresetSelectorProps) => {
  const { width } = useViewport();
  const isMobile = width < mobileBreakpoint;
  const track = useAmplitudeTracking();

  const selectedOption = PRESET_OPTIONS.find((o) => o.value === preset) || null;

  const allowedGranularities =
    PRESETS.find((p) => p.key === preset)?.allowedGranularities ??
    GRANULARITY_OPTIONS.map((g) => g.value);

  const handlePresetChange = (option: LilliSelectOption | null) => {
    if (!option) return;
    const key = option.value as PresetKey;
    setPreset(key);
    const presetConfig = PRESETS.find((p) => p.key === key);
    if (presetConfig) {
      setGranularity(presetConfig.defaultGranularity);
    }
    track("Install Performance", {
      action: "time_period_selected",
      time_period: key,
      previous_time_period: preset,
      granularity: presetConfig?.defaultGranularity ?? granularity,
    });
  };

  const handleGranularityChange = (g: Granularity) => {
    setGranularity(g);
    track("Install Performance", {
      action: "granularity_selected",
      granularity: g,
      previous_granularity: granularity,
      time_period: preset,
    });
  };

  const getButtonStyle = (
    g: Granularity,
    isDisabled: boolean
  ): React.CSSProperties => {
    const isActive = g === granularity;
    if (isDisabled) {
      return {
        border: `1px solid ${theme.neutral2}`,
        backgroundColor: theme.neutral1,
        color: theme.neutral4,
        borderRadius: isMobile ? 3 : 4,
        padding: isMobile ? "4px 6px" : "8px 16px",
        fontWeight: 400,
        minWidth: 35,
      };
    }
    return {
      border: `1px solid ${isActive ? theme.primary1 : theme.neutral2}`,
      backgroundColor: isActive ? theme.primary1 : theme.white,
      color: theme.neutral7,
      borderRadius: isMobile ? 3 : 4,
      padding: isMobile ? "4px 6px" : "8px 16px",
      fontWeight: isActive ? 500 : 400,
      minWidth: 35,
    };
  };

  return (
    <div className={styles.container}>
      <div className={styles.controls}>
        <div className={styles.preset_select}>
          <LilliSelect
            options={PRESET_OPTIONS}
            value={selectedOption}
            onChange={handlePresetChange}
            placeholder="Select time range"
            name="preset-selector"
            // Cancel LilliSelect's built-in 4px top margin so the control
            // aligns with the granularity buttons in the same row.
            inputStyles={{ marginTop: 0 }}
            // This selector sits inside a sticky header (z-index 10). Without
            // withinModal the portaled menu has z-index auto and its first
            // option is painted beneath the header.
            withinModal
          />
        </div>
        <div className={styles.granularity}>
          {GRANULARITY_OPTIONS.map((g) => {
            const isDisabled = !allowedGranularities.includes(g.value);
            return (
              <PrimaryButton
                key={g.value}
                style={getButtonStyle(g.value, isDisabled)}
                onClick={() => handleGranularityChange(g.value)}
                disabled={isDisabled}
              >
                {isMobile ? g.short : g.label}
              </PrimaryButton>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DatePresetSelector;
